import { useCallback } from 'react';

import {
  hasRangeOnChange,
  hasSingleOnChange,
  setDatePreservingTime,
  setTimePreservingDate,
} from '../utils';

import type {
  CalendarProps,
  RangeDatePickerChangeHandler,
  SingleDatePickerChangeHandler,
} from '../types';

interface UseDatePickerHandlersOptions {
  finalShowTime: boolean;
  onClose: () => void;
  props: CalendarProps;
  shouldCloseOnSelect: boolean;
  canonicalizeDate?: (date: Date | null) => Date | null;
}

/**
 * DatePicker의 날짜/시간 변경 핸들러를 제공하는 커스텀 훅
 *
 * @description
 * - Single DatePicker 날짜 변경
 * - Range DatePicker 날짜 변경
 * - TimePicker 시간 변경
 * - 날짜 선택 시 시간 정보 보존 처리
 * - shouldCloseOnSelect에 따른 자동 닫기
 *
 * @param options - 핸들러 옵션
 * @returns 날짜/시간 변경 핸들러
 */
export const useDatePickerHandlers = (options: UseDatePickerHandlersOptions) => {
  const { props, finalShowTime, shouldCloseOnSelect, onClose, canonicalizeDate } = options;

  // Single DatePicker 날짜 변경 핸들러
  const handleSingleDateChange: SingleDatePickerChangeHandler = useCallback(
    (date, event) => {
      const normalizedDate = canonicalizeDate
        ? canonicalizeDate(date)
        : setDatePreservingTime(date, {
            showTime: finalShowTime,
            referenceDate: props.selected,
          });

      // 타입 가드를 사용하여 자동 타입 추론 (캐스팅 불필요)
      if (hasSingleOnChange(props)) {
        props.onChange(normalizedDate, event);
      }

      if (shouldCloseOnSelect) {
        onClose();
      }
    },
    [props, finalShowTime, shouldCloseOnSelect, onClose, canonicalizeDate],
  );

  // Range DatePicker 날짜 변경 핸들러
  const handleRangeDateChange: RangeDatePickerChangeHandler = useCallback(
    (dates, event) => {
      const [startDate, endDate] = dates;
      const normalizedStartDate = canonicalizeDate
        ? canonicalizeDate(startDate)
        : setDatePreservingTime(startDate, {
            showTime: finalShowTime,
            referenceDate: props.startDate,
          });
      const normalizedEndDate = canonicalizeDate
        ? canonicalizeDate(endDate)
        : setDatePreservingTime(endDate, {
            showTime: finalShowTime,
            referenceDate: props.endDate,
          });

      // 타입 가드를 사용하여 자동 타입 추론 (캐스팅 불필요)
      if (hasRangeOnChange(props)) {
        props.onChange([normalizedStartDate, normalizedEndDate], event);
      }

      if (shouldCloseOnSelect && endDate) {
        onClose();
      }
    },
    [props, finalShowTime, shouldCloseOnSelect, onClose, canonicalizeDate],
  );

  // 시간 변경 핸들러: 날짜 정보는 유지하고 시간만 변경
  const handleTimeChange = useCallback(
    (time: Date | null) => {
      if (!time) return;

      const newDate = setTimePreservingDate(time, props.selected);

      // 타입 가드를 사용하여 자동 타입 추론 (캐스팅 불필요)
      if (hasSingleOnChange(props)) {
        props.onChange(newDate, undefined);
      }
    },
    [props],
  );

  return {
    handleSingleDateChange,
    handleRangeDateChange,
    handleTimeChange,
  };
};
